import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from '@/lib/axios';


interface CartItem {
  id: string;
  owner_id: number;
  user_id: number;
  product_id: number;
  slug: string;
  product_name: string;
  product_thumbnail_image: string | null;
  is_preorder: number;
  variation: any; // use a more specific type if known
  price: number;
  currency_symbol: string;
  tax: number;
  shipping_cost: number;
  quantity: number;
  lower_limit: number;
  upper_limit: number;
}

interface CartState {
  items: CartItem[];
  loading: boolean;
  error: string | null;
}

const initialState: CartState = {
  items: [],
  loading: false,
  error: null,
};

// Async Thunks
export const fetchCartItems = createAsyncThunk(
  'cart/fetchCartItems',
  async () => {
    const response = await axios.post('/api/v2/carts');
    return response.data[0].cart_items;
  }
);

export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async (
    {
      id,
      userId,
      quantity,
      preorder_available,
      influencer_id,
    }: {
      influencer_id: string;
      id: string;
      userId?: string;
      quantity?: number;
      preorder_available: boolean;
    },
    { dispatch }
  ) => {
    const response = await axios.post('/api/v2/web/carts/add', {
      id,
      user_id: userId,
      quantity: quantity || 1,
      is_preorder: preorder_available,
      influencer_id,
    });

    await dispatch(fetchCartItems());
    return response.data; // Return data
  }
);

export const updateCartQuantity = createAsyncThunk(
  'cart/updateQuantity',
  async ({ id, quantity }: { id: string; quantity: number }) => {
    await axios.post('/api/v2/carts/change-quantity', {
      id,
      quantity,
    });
    return { id, quantity };
  }
);

export const deleteCartItem = createAsyncThunk(
  'cart/deleteItem',
  async (id: string) => {
    await axios.delete(`/api/v2/carts/${id}`);
    return id;
  }
);

// Redux slice
const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearCart: (state) => {
      state.items = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCartItems.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchCartItems.fulfilled,
        (state, action: PayloadAction<CartItem[]>) => {
          state.loading = false;
          state.items = action.payload;
        }
      )
      .addCase(fetchCartItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch cart items';
      })

      .addCase(
        addToCart.fulfilled,
        (state, action: PayloadAction<CartItem>) => {
          console.log(state, action);
        }
      )

      .addCase(
        updateCartQuantity.fulfilled,
        (state, action: PayloadAction<{ id: string; quantity: number }>) => {
          const item = state.items.find(
            (item) => item.id === action.payload.id
          );
          if (item) {
            item.quantity = action.payload.quantity;
          }
        }
      )

      .addCase(
        deleteCartItem.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.items = state.items.filter(
            (item) => item.id !== action.payload
          );
        }
      );
  },
});

export const { clearCart } = cartSlice.actions;
export default cartSlice.reducer;
